import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Navigation from '../../components/feature/Navigation';
import Button from '../../components/base/Button';
import LanguageSelector from '../../components/common/LanguageSelector';
import { useStopwatch } from '../../hooks/useStopwatch';
import { formatTime } from '../../utils/timeFormat';
import { getSEOConfig, updateSEOTags } from '../../i18n/utils/seo';
import type { SupportedLanguage } from '../../i18n/utils/language';

export default function Stopwatch() {
  const { t, i18n } = useTranslation(['stopwatch', 'common']);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  
  const { time, isRunning, start, pause, reset } = useStopwatch({
    t,
    language: i18n.language,
    soundEnabled
  });


  const handleStart = () => {
    start();
    // Desktop notification
    if ('Notification' in window && Notification.permission === 'granted') {
      new Notification(t('common:message.stopwatchStarted'), {
        body: t('common:message.stopwatchStartedDesc'),
        icon: '/favicon.png'
      });
    }
  };

  const handlePause = () => {
    pause();
    // Desktop notification
    if ('Notification' in window && Notification.permission === 'granted') {
      new Notification(t('common:message.stopwatchStopped'), {
        body: t('common:message.stopwatchStoppedDesc'),
        icon: '/favicon.png'
      });
    }
  };

  const handleReset = () => {
    reset();
    // Desktop notification
    if ('Notification' in window && Notification.permission === 'granted') {
      new Notification(t('common:message.stopwatchReset'), {
        body: t('common:message.stopwatchResetDesc'),
        icon: '/favicon.png'
      });
    }
  };

  const requestNotificationPermission = () => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  };

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  const handleFullscreenKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setIsFullscreen(false);
    }
  };

  // 随机排序用户评价
  const [randomizedReviews, setRandomizedReviews] = useState<Array<{
    id: number;
    avatar: string;
    name: string;
    rating: number;
    review: string;
  }>>([]);

  useEffect(() => {
    // 更新SEO配置
    const currentLang = i18n.language as SupportedLanguage;
    const seoConfig = getSEOConfig('stopwatch', currentLang);
    updateSEOTags(seoConfig, currentLang);
    
    // 请求通知权限
    requestNotificationPermission();
  }, [i18n.language]);

  useEffect(() => {
    // 创建用户评价数据，使用当前语言的翻译
    const userReviews = [
      {
        id: 1,
        avatar: "/images/avatars/Michael Runner.jpg",
        name: t('user.michaelRunner'),
        rating: 5,
        review: t('reviews.michaelRunner')
      },
      {
        id: 2,
        avatar: "/images/avatars/Chef Johnson.jpg",
        name: t('user.chefJohnson'),
        rating: 5,
        review: t('reviews.chefJohnson')
      },
      {
        id: 3,
        avatar: "/images/avatars/Sarah Student.jpg",
        name: t('user.sarahStudent'),
        rating: 4,
        review: t('reviews.sarahStudent')
      },
      {
        id: 4,
        avatar: "/images/avatars/Coach Williams.jpg",
        name: t('user.coachWilliams'),
        rating: 5,
        review: t('reviews.coachWilliams')
      },
      {
        id: 5,
        avatar: "/images/avatars/Dr. Anderson.jpg",
        name: t('user.drAnderson'),
        rating: 5,
        review: t('reviews.drAnderson')
      }
    ];

    // 只在语言改变时重新生成和随机排序，但保持同一语言下的稳定性
    const shuffled = [...userReviews].sort(() => Math.random() - 0.5);
    setRandomizedReviews(shuffled);
  }, [t, i18n.language]);

  // Fullscreen modal
  if (isFullscreen) {
    return (
      <div 
        className="fixed inset-0 bg-black bg-opacity-95 z-50 flex items-center justify-center p-4"
        onClick={toggleFullscreen}
        onKeyDown={handleFullscreenKeyPress}
        tabIndex={0}
      >
        <div className="text-center w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
          {/* Exit button */}
          <button
            onClick={toggleFullscreen}
            className="absolute top-4 right-4 md:top-6 md:right-6 text-white hover:text-gray-300 transition-colors z-10"
          >
            <i className="ri-close-line text-2xl md:text-3xl"></i>
          </button>
          
          {/* Large time display */}
          <div className="mb-12 md:mb-12">
            <div className="text-6xl sm:text-7xl md:text-9xl font-mono font-[700] text-green-400 mb-12 md:mb-8 whitespace-nowrap">
              {formatTime(time)}
            </div>
            
            {/* Control buttons */}
            <div className="flex items-center justify-center space-x-3 md:space-x-6">
              {!isRunning ? (
                <Button onClick={handleStart} size="lg" className="px-4 py-2.5 md:px-8 md:py-4 text-base md:text-xl bg-green-600 hover:bg-green-700">
                  <i className="ri-play-fill mr-2 md:mr-3 text-base md:text-xl"></i>
                  {t('common:button.start')}
                </Button>
              ) : (
                <Button onClick={handlePause} variant="secondary" size="lg" className="px-4 py-2.5 md:px-8 md:py-4 text-base md:text-xl">
                  <i className="ri-pause-fill mr-2 md:mr-3 text-base md:text-xl"></i>
                  {t('common:button.pause')}
                </Button>
              )}
              <Button onClick={handleReset} variant="danger" size="lg" className="px-4 py-2.5 md:px-8 md:py-4 text-base md:text-xl">
                <i className="ri-refresh-line mr-2 md:mr-3 text-base md:text-xl"></i>
                {t('common:button.reset')}
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />
      
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Main stopwatch display */}
        <div className="bg-white rounded-2xl shadow-lg p-4 md:p-8 mb-6 md:mb-8">
          <div className="text-center">
            <div className="flex justify-between items-start mb-6 md:mb-8">
              <h2 className="text-xl md:text-2xl font-bold text-gray-800">{t('page.title')}</h2>
              
              {/* Sound switch and fullscreen button - moved to top right */}
              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-2">
                  <span className="hidden md:block text-sm text-gray-600">{t('common:label.sound')}</span>
                  <span className="md:hidden text-sm text-gray-600">{t('common:label.sound')}</span>
                  <button
                    onClick={() => setSoundEnabled(!soundEnabled)}
                    className={`relative w-12 h-6 rounded-full transition-colors ${
                      soundEnabled ? 'bg-green-600' : 'bg-gray-300'
                    }`}
                  >
                    <div
                      className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
                        soundEnabled ? 'transform translate-x-7' : 'transform translate-x-1'
                      }`}
                    />
                  </button>
                </div>
                
                <button
                  onClick={toggleFullscreen}
                  className="text-gray-600 hover:text-green-600 transition-colors p-2 hover:bg-gray-100 rounded-lg flex items-center space-x-2"
                  title="Fullscreen"
                >
                  <span className="hidden md:block text-sm font-medium">{t('common:label.fullscreen')}</span>
                  <i className="ri-picture-in-picture-2-line text-xl"></i>
                </button>
              </div>
            </div>
            
            {/* Time display container - unified height structure */}
            <div className="mb-8 md:mb-10">
              <div className="flex items-center justify-center mb-6 md:mb-8">
                <div className="text-6xl md:text-8xl font-mono font-bold text-green-600">
                  {formatTime(time)}
                </div>
              </div>
              
              {/* Control buttons */}
              <div className="flex items-center justify-center space-x-4">
                {!isRunning ? (
                  <Button onClick={handleStart} size="md" className="px-6 md:px-8 bg-green-600 hover:bg-green-700 whitespace-nowrap">
                    <i className="ri-play-fill mr-2"></i>
                    {t('common:button.start')}
                  </Button>
                ) : (
                  <Button onClick={handlePause} variant="secondary" size="md" className="px-6 md:px-8 whitespace-nowrap">
                    <i className="ri-pause-fill mr-2"></i>
                    {t('common:button.pause')}
                  </Button>
                )}
                <Button onClick={handleReset} variant="danger" size="md" className="px-6 md:px-8 whitespace-nowrap">
                  <i className="ri-refresh-line mr-2"></i>
                  {t('common:button.reset')}
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* How to use */}
        <div className="bg-white rounded-2xl shadow-lg p-4 md:p-8 mb-6 md:mb-8">
          <h3 className="text-lg md:text-xl font-bold text-gray-800 mb-4 md:mb-6">{t('howto.title')}</h3>
          <div className="space-y-3 md:space-y-4 text-gray-600">
            <div className="flex items-start space-x-3">
              <div className="w-6 h-6 flex items-center justify-center bg-green-100 text-green-600 rounded-full text-sm font-bold mt-0.5 flex-shrink-0">1</div>
              <p className="text-sm md:text-base">{t('howto.step1')}</p>
            </div>
            <div className="flex items-start space-x-3">
              <div className="w-6 h-6 flex items-center justify-center bg-green-100 text-green-600 rounded-full text-sm font-bold mt-0.5 flex-shrink-0">2</div>
              <p className="text-sm md:text-base">{t('howto.step2')}</p>
            </div>
            <div className="flex items-start space-x-3">
              <div className="w-6 h-6 flex items-center justify-center bg-green-100 text-green-600 rounded-full text-sm font-bold mt-0.5 flex-shrink-0">3</div>
              <p className="text-sm md:text-base">{t('howto.step3')}</p>
            </div>
            <div className="flex items-start space-x-3">
              <div className="w-6 h-6 flex items-center justify-center bg-green-100 text-green-600 rounded-full text-sm font-bold mt-0.5 flex-shrink-0">4</div>
              <p className="text-sm md:text-base">{t('howto.step4')}</p>
            </div>
          </div>
        </div>

        {/* User reviews module */}
        <div className="bg-white rounded-2xl shadow-lg p-4 md:p-8 mb-6 md:mb-8">
          <h3 className="text-lg md:text-xl font-bold text-gray-800 mb-4 md:mb-6">{t('reviews.title')}</h3>
          <div className="relative overflow-hidden">
            <div className="flex overflow-x-auto space-x-4 md:space-x-8 scrollbar-hide">
              {randomizedReviews.map((review) => (
                <div key={review.id} className="flex-shrink-0 bg-gray-50 p-4 md:p-6 rounded-lg w-80">
                  <div className="flex items-center mb-3 md:mb-4">
                    <img 
                      src={review.avatar}
                      alt={`${review.name} avatar`}
                      className="w-10 h-10 md:w-12 md:h-12 rounded-full object-cover mr-3"
                    />
                    <div className="flex-1">
                      <div className="flex text-yellow-400 mb-1">
                        {[...Array(5)].map((_, i) => (
                          <i key={i} className={`${i < review.rating ? 'ri-star-fill' : 'ri-star-line'} text-sm`}></i>
                        ))}
                      </div>
                      <span className="text-xs md:text-sm text-gray-600 font-medium">{review.name}</span>
                    </div>
                  </div>
                  <p className="text-gray-700 text-sm md:text-base">{review.review}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Footer */}
        <footer className="bg-gray-100 text-gray-800 rounded-2xl p-6 md:p-8">
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center space-y-6 lg:space-y-0">
            {/* Left side - Website description */}
            <div className="lg:w-2/3">
              <h4 className="text-lg md:text-xl font-bold text-gray-800 mb-4 md:mb-6">{t('common:footer.about')}</h4>
              <p className="text-sm text-gray-600 leading-relaxed mb-4">
                {t('common:footer.aboutDesc')}
              </p>
              <div className="space-y-3 text-sm text-gray-600">
                <div className="flex items-start space-x-3">
                  <i className="ri-timer-line text-blue-500 mt-0.5 flex-shrink-0"></i>
                  <div>
                    <span className="font-medium text-gray-900">{t('common:nav.timer')}</span> - {t('common:footer.timerDesc')}
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <i className="ri-play-circle-line text-green-500 mt-0.5 flex-shrink-0"></i>
                  <div>
                    <span className="font-medium text-gray-900">{t('common:nav.stopwatch')}</span> - {t('common:footer.stopwatchDesc')}
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <i className="ri-global-line text-purple-500 mt-0.5 flex-shrink-0"></i>
                  <div>
                    <span className="font-medium text-gray-900">{t('common:nav.worldclock')}</span> - {t('common:footer.worldClockDesc')}
                  </div>
                </div>
              </div>
              <div className="mt-4">
                <div className="flex flex-wrap items-center gap-4 text-xs text-gray-500">
                  <a href="/terms" className="hover:text-gray-700 transition-colors">{t('common:footer.termsOfService')}</a>
                  <span>|</span>
                  <a href="#" className="hover:text-gray-700 transition-colors">{t('common:footer.privacy')}</a>
                  <span>|</span>
                  <a href="mailto:mu9347mu@gmail.com?subject=Contact Us - timer.today&body=Hello timer.today team,%0D%0A%0D%0AI would like to..." className="hover:text-gray-700 transition-colors">{t('common:footer.contactUs')}</a>
                  <span>|</span>
                  <span>{t('common:footer.copyright')}</span>
                  <span className="lg:hidden">|</span>
                  <div className="lg:hidden">
                    <LanguageSelector />
                  </div>
                </div>
              </div>
            </div>

              {/* Vertical divider - only visible on large screens */}
              <div className="hidden lg:flex w-px bg-gray-300 mx-6 h-40 self-center"></div>

            {/* Right side - Multilingual content */}
              <div className="hidden lg:block lg:w-1/3">
              <div className="space-y-3 text-sm text-gray-600 mt-12">
                <p>stopwatch timer precision timing what time is it now</p>
                <p>ストップウォッチ タイマー 精密計時 今何時</p>
                <p>cronómetro temporizador tiempo preciso qué hora</p>
                <p>Stoppuhr Timer präzise Zeit wie spät</p>
                <p>секундомер таймер точное время который час</p>
                <LanguageSelector />
              </div>
            </div>
          </div>
        </footer>
        </div>
    </div>
  );
}
